import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Stack,
  Alert,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { questions } from '../../services/api';
import { Question, QuestionOption, QuestionType } from '../../types';
import CreateQuestionDialog from '../../components/questions/CreateQuestionDialog';
import { QuestionEditor } from '../../components/questions/QuestionEditor';
import { QuestionTypeSelect } from '../../components/questions/QuestionTypeSelect';

interface NewQuestion {
  text: string;
  category: string;
  question_type: QuestionType;
  low_text: string;
  high_text: string;
  options: QuestionOption[];
}

const QuestionManagement: React.FC = () => {
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newQuestion, setNewQuestion] = useState<NewQuestion>({
    text: '',
    category: '',
    question_type: 'multiple_choice',
    low_text: '',
    high_text: '',
    options: []
  });

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const response = await questions.getAll();
      setAllQuestions(response.data);
    } catch (err) {
      setError('Failed to load questions');
      console.error('Failed to load questions:', err);
    }
  };

  const handleCreateQuestion = async (newQuestion: any) => {
    try {
      setIsSubmitting(true);
      await questions.create(newQuestion);
      await loadQuestions();
      setCreateDialogOpen(false);
    } catch (err) {
      setError('Failed to create question');
      console.error('Failed to create question:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditQuestion = async () => {
    if (!selectedQuestion || !selectedQuestion.id) return;

    setIsSubmitting(true);
    try {
      await questions.update(selectedQuestion.id, selectedQuestion);
      setEditDialogOpen(false);
      setSelectedQuestion(null);
      loadQuestions();
    } catch (err) {
      setError('Failed to update question');
      console.error('Failed to update question:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion || !selectedQuestion.id) return;

    setIsSubmitting(true);
    try {
      await questions.deactivate(selectedQuestion.id);
      setDeleteDialogOpen(false);
      setSelectedQuestion(null);
      loadQuestions();
    } catch (err) {
      setError('Failed to deactivate question');
      console.error('Failed to deactivate question:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOption = () => {
    const newOption: QuestionOption = {
      id: Date.now(), // Temporary ID for new option
      front_text: '',
      back_text: '',
      value: 0,
      order: 0
    };

    if (createDialogOpen) {
      setNewQuestion(prev => ({
        ...prev,
        options: [...prev.options, newOption]
      }));
    } else if (editDialogOpen && selectedQuestion) {
      setSelectedQuestion(prev => ({
        ...prev!,
        options: [...(prev?.options || []), newOption]
      }));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Question Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Question
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <List>
          {allQuestions.map((question) => (
            <ListItem
              key={question.id}
              divider
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                opacity: question.is_active ? 1 : 0.5,
              }}
            >
              <ListItemText
                primary={question.text}
                secondary={
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label={question.category} size="small" />
                    <Chip label={question.question_type} size="small" />
                    {!question.is_active && (
                      <Chip label="Inactive" size="small" color="error" />
                    )}
                  </Stack>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => {
                    setSelectedQuestion(question);
                    setEditDialogOpen(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label={question.is_active ? "deactivate" : "activate"}
                  onClick={() => {
                    setSelectedQuestion(question);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <CreateQuestionDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setError(null);
        }}
        onSubmit={handleCreateQuestion}
        isSubmitting={isSubmitting}
        error={error}
      />

      {/* Edit Question Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <QuestionEditor
              question={{
                text: selectedQuestion.text,
                category: selectedQuestion.category,
                question_type: selectedQuestion.question_type as QuestionType,
                low_text: selectedQuestion.low_text || '',
                high_text: selectedQuestion.high_text || '',
                key: selectedQuestion.key || '',
                options: (selectedQuestion.options || []).map(opt => ({
                  front_text: opt.front_text,
                  back_text: opt.back_text,
                  text: opt.front_text
                })),
                video_url: selectedQuestion.video_url,
                visual_component_url: selectedQuestion.visual_component_url
              }}
              onChange={(updatedQuestion) => {
                let formattedOptions: QuestionOption[] = [];
                if (updatedQuestion.question_type === 'multiple_reveal') {
                  formattedOptions = updatedQuestion.options.map((option, index) => ({
                    front_text: option.front_text || '',
                    back_text: option.back_text || '',
                    id: 0,
                    value: 0,
                    order: index
                  }));
                } else if (['multiple_choice', 'multiple_select'].includes(updatedQuestion.question_type)) {
                  formattedOptions = updatedQuestion.options.map((option, index) => ({
                    front_text: option.text || '',
                    back_text: '',
                    id: 0,
                    value: 0,
                    order: index
                  }));
                } else if (updatedQuestion.question_type === 'drag_and_drop') {
                  formattedOptions = updatedQuestion.options.map((option, index) => ({
                    front_text: option.front_text || '',
                    back_text: '',
                    id: 0,
                    value: 0,
                    order: index
                  }));
                }
                
                setSelectedQuestion({
                  ...selectedQuestion,
                  ...updatedQuestion,
                  options: formattedOptions,
                  key: updatedQuestion.key
                });
              }}
              showCategory={true}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditQuestion} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Deactivate Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate this question? This will hide it from future use but preserve all historical data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteQuestion} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuestionManagement;
